import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import { Shell } from "../layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "forgot") {
      await fetch(`${API}/auth/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: form.email }) });
      setLinkSent(true);
      return;
    }
    const body = mode === "login" ? { email: form.email, password: form.password } : form;
    const r = await fetch(`${API}/auth/${mode === "login" ? "login" : "register"}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = data.detail;
      setError(typeof detail === "string" ? detail : t("auth.errGeneric"));
      return;
    }
    setUser(data.user);
    navigate("/account");
  };

  const heading = mode === "login"
    ? <>{t("auth.loginTitleA")}<br /><em>{t("auth.loginTitleB")}</em></>
    : mode === "register"
      ? <>{t("auth.regTitleA")}<br /><em>{t("auth.regTitleB")}</em></>
      : <>{t("auth.resetTitle")}</>;

  return (
    <Shell>
      <main className="admin-login">
        <div className="login-card">
          <span className="brand-mark">NN</span>
          <p className="eyebrow">{t("auth.eyebrow")}</p>
          <h1>{heading}</h1>
          {mode === "forgot" && linkSent ? (
            <div className="success-state" data-testid="forgot-success-message">
              <span>✓</span>
              <p>{t("auth.linkSent")}</p>
              <button className="button button-outline" onClick={() => { setMode("login"); setLinkSent(false); }} data-testid="back-to-login-button">{t("auth.backToLogin")}</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="customer-auth-form">
              {mode === "forgot" && <p className="auth-note">{t("auth.resetCopy")}</p>}
              {mode === "register" && (
                <>
                  <label>{t("auth.name")}<input required minLength="2" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" /></label>
                  <label>{t("auth.phone")} <span>{t("auth.phoneOpt")}</span><input value={form.phone} placeholder="+370 6XX XXXXX" onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" /></label>
                </>
              )}
              <label>{t("auth.email")}<input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" /></label>
              {mode !== "forgot" && <label>{t("auth.password")}<input type="password" required minLength="6" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" /></label>}
              {error && <p className="form-error" data-testid="auth-error">{error}</p>}
              <button className="button button-red full" data-testid="auth-submit-button">{mode === "login" ? t("auth.signIn") : mode === "register" ? t("auth.createAccount") : t("auth.sendLink")} <ArrowUpRight size={17} /></button>
            </form>
          )}
          <p className="auth-switch" data-testid="auth-switch">
            {mode === "login" && <>{t("auth.newHere")} <button type="button" onClick={() => { setMode("register"); setError(""); }} data-testid="switch-to-register-button">{t("auth.createAccount")}</button> · <button type="button" onClick={() => { setMode("forgot"); setError(""); setLinkSent(false); }} data-testid="forgot-password-link">{t("auth.forgotLink")}</button></>}
            {mode === "register" && <>{t("auth.haveAccount")} <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="switch-to-login-button">{t("auth.signIn")}</button></>}
            {mode === "forgot" && !linkSent && <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="forgot-back-button">{t("auth.backToLogin")}</button>}
          </p>
        </div>
      </main>
    </Shell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState("form"); // form | done | invalid
  const [error, setError] = useState("");
  const { t } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError(t("auth.passwordsMismatch"));
    const r = await fetch(`${API}/auth/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, password: form.password }) });
    if (!r.ok) return setState("invalid");
    setState("done");
  };

  return (
    <Shell>
      <main className="admin-login">
        <div className="login-card" data-testid="reset-password-page">
          <span className="brand-mark">NN</span>
          <p className="eyebrow">{t("auth.eyebrow")}</p>
          {state === "done" ? (
            <div className="success-state" data-testid="reset-success-message">
              <span>✓</span>
              <h1>{t("auth.resetDone")}</h1>
              <Link to="/login" className="button button-red full" data-testid="reset-login-link">{t("auth.signIn")}</Link>
            </div>
          ) : state === "invalid" ? (
            <div className="success-state" data-testid="reset-invalid-message">
              <h1>{t("auth.resetInvalid")}</h1>
              <Link to="/login" className="button button-outline full" data-testid="reset-back-link">{t("auth.backToLogin")}</Link>
            </div>
          ) : (
            <>
              <h1>{t("auth.newPasswordTitle")}</h1>
              <form onSubmit={submit} data-testid="reset-password-form">
                <label>{t("auth.newPassword")}<input type="password" required minLength="6" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="reset-password-input" /></label>
                <label>{t("auth.confirmPassword")}<input type="password" required minLength="6" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} data-testid="reset-confirm-input" /></label>
                {error && <p className="form-error" data-testid="reset-error">{error}</p>}
                <button className="button button-red full" data-testid="reset-submit-button">{t("auth.setPassword")} <ArrowUpRight size={17} /></button>
              </form>
            </>
          )}
        </div>
      </main>
    </Shell>
  );
}
